import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useAuth } from '@shared/components/AuthProvider';
import { useVerifyAccountEmailMutation } from '@graphql/mutations/user.graphql.generated';
import { useEmailVerificationGate } from '@shared/hooks/useEmailVerificationGate';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { withWindow } from '@shared/utils/withWindow';
import { addError } from '@shared/utils/logger';
import { config } from '@static/js/env.config';
import { EmailVerificationStatusDocument } from './EmailVerificationStatus.graphql.generated';
import type { EmailVerificationStatusQuery } from './EmailVerificationStatus.graphql.generated';
import type { EmailVerificationStep } from './EmailVerificationDialog.types';

// Return the user to a canonical /account/* URL on the auth origin after Auth0
// verifies their email. Passing the raw publish-page URL (e.g. the guest site)
// is not an allowlisted Auth0 callback and bounces the user to the login page.
const VERIFY_EMAIL_RETURN_URL = config.clientUri + '/account/personal-info?dialog=verifyEmailSuccess';

// Cooldown (seconds) after a send before "Resend" is allowed again, so users can't
// flood identity_service / Auth0 by hammering the button.
const RESEND_COOLDOWN_SECONDS = 30;

// After "I have verified my email", wait briefly before the live read so Auth0 has time
// to propagate the freshly-verified status. If it comes back unverified, the caller lets
// the user retry, so a short delay is enough — we no longer need a long upfront window.
const VERIFY_PROPAGATION_DELAY_MS = 5000;

type UseEmailVerificationFlowParams = Readonly<{
  /** Called once, when a status re-check confirms the address is verified. */
  onVerified?: () => void;
}>;

/**
 * The email-verification state machine, with no UI of its own: sending, the resend cooldown, the
 * Auth0 propagation delay and the single throttled live status read.
 *
 * Two surfaces drive publishing through it — the standalone `EmailVerificationDialog` and the
 * Go Live Checks wizard's first step — and the Auth0/identity_service rules encoded here (one send
 * per in-flight request, 30s between resends, one live read per confirm) are the reason they share
 * this rather than each holding their own copy.
 */
export const useEmailVerificationFlow = ({ onVerified }: UseEmailVerificationFlowParams = {}) => {
  const { currentUser, identifyCurrentUser } = useAuth();
  const { isEmailVerified } = useEmailVerificationGate();
  const [isPublishEmailVerificationEnabled] = useIsFeatureEnabled('publishEmailVerificationEnabled');
  const email = currentUser.profile?.email ?? undefined;

  const client = useApolloClient();
  const [sendVerifyEmail, { loading: isSending }] = useVerifyAccountEmailMutation();

  const [step, setStep] = useState<EmailVerificationStep>('intro');
  const [isChecking, setIsChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);
  // Apart from `checkFailed`: the status read erroring is not the same as a real "still unverified".
  const [checkErrored, setCheckErrored] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  // A resend otherwise only moves the cooldown, which reads as nothing having happened.
  const [didResend, setDidResend] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Guards against a second send firing in the async gap before the button disables.
  const isSendingRef = useRef(false);
  // Mirrors resendCooldown so the resend guard can read it without re-creating handleResend each tick.
  const resendCooldownRef = useRef(0);
  // Tracks the "verifying" propagation delay so it can be cancelled if the surface closes/unmounts.
  const verifyTimeoutRef = useRef<number | null>(null);
  // Monotonic per-attempt token. Each confirm bumps it; cancel/unmount bumps it too, so a stale
  // in-flight attempt sees a changed token and bails instead of settling a later session.
  const verifyAttemptRef = useRef(0);

  const handleVerified = useEventCallback(() => onVerified?.());

  // Cancel any pending verify delay when the hook unmounts.
  useEffect(() => {
    return () => {
      // Intentional ref mutation on unmount: invalidate any in-flight attempt so it can't settle.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      verifyAttemptRef.current++;
      withWindow(global => {
        if (verifyTimeoutRef.current) {
          global.clearTimeout(verifyTimeoutRef.current);
        }
      });
    };
  }, []);

  // Keep the ref in sync so the resend guard reads the latest cooldown without a
  // dependency, then tick the cooldown down to zero one second at a time.
  useEffect(() => {
    resendCooldownRef.current = resendCooldown;
    if (resendCooldown <= 0) {
      return undefined;
    }
    const timeoutId = withWindow<number | null>(global => global.setTimeout(() => setResendCooldown(seconds => seconds - 1), 1000), null);
    return () =>
      withWindow(global => {
        if (timeoutId) {
          global.clearTimeout(timeoutId);
        }
      });
  }, [resendCooldown]);

  /** Puts the machine back at its first step. Call when a surface starts a fresh session. */
  const reset = useCallback(() => {
    setStep('intro');
    setCheckFailed(false);
    setCheckErrored(false);
    setSendFailed(false);
    setDidResend(false);
    setResendCooldown(0);
    setIsChecking(false);
  }, []);

  /** Invalidates any in-flight confirm so a late resolution can't settle a session that's over. */
  const cancel = useCallback(() => {
    verifyAttemptRef.current++;
    withWindow(global => {
      if (verifyTimeoutRef.current) {
        global.clearTimeout(verifyTimeoutRef.current);
      }
    });
    setIsChecking(false);
  }, []);

  // Sends the verification email exactly once per in-flight request. A concurrent
  // call (e.g. a rapid double-click before the button disables) is collapsed into a no-op
  // so identity_service / Auth0 only ever receives a single send. Returns true only when this
  // call actually sent, so duplicate callers don't mistake the no-op for a successful send.
  const sendEmail = useCallback(async (): Promise<boolean> => {
    if (isSendingRef.current) {
      return false;
    }
    isSendingRef.current = true;
    try {
      await sendVerifyEmail({ variables: { payload: { returnUrl: VERIFY_EMAIL_RETURN_URL } } });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      return true;
    } finally {
      isSendingRef.current = false;
    }
  }, [sendVerifyEmail]);

  const handleVerify = useCallback(async () => {
    setCheckFailed(false);
    setCheckErrored(false);
    setSendFailed(false);
    try {
      // Only advance on a real send; a de-duped duplicate leaves the in-flight call to govern the step.
      const sent = await sendEmail();
      if (sent) {
        setStep('sent');
      }
    } catch {
      // Stay on the intro step and surface the failure so the user can retry.
      setSendFailed(true);
    }
  }, [sendEmail]);

  const handleResend = useCallback(async () => {
    // Ignore resends while the cooldown is active.
    if (resendCooldownRef.current > 0) {
      return;
    }
    setCheckFailed(false);
    setCheckErrored(false);
    setSendFailed(false);
    setDidResend(false);
    try {
      if (await sendEmail()) setDidResend(true);
    } catch {
      setSendFailed(true);
    }
  }, [sendEmail]);

  const handleConfirmVerified = useCallback(async () => {
    const attempt = ++verifyAttemptRef.current;
    setCheckFailed(false);
    setCheckErrored(false);
    setDidResend(false);
    setIsChecking(true);

    // Brief "verifying" pause so Auth0 can propagate the freshly-verified status before
    // we spend the single (throttled) confirmVerification live read.
    await new Promise<void>(resolve => {
      verifyTimeoutRef.current = withWindow<number | null>(global => global.setTimeout(resolve, VERIFY_PROPAGATION_DELAY_MS), null);
    });
    if (attempt !== verifyAttemptRef.current) {
      return;
    }

    try {
      // Re-fetch verification status fresh from the server. confirmVerification (in the query)
      // bypasses the BE's 60s email-verification cache and reads Auth0 live, and no-cache
      // bypasses the Apollo client cache.
      const { data } = await client.query<EmailVerificationStatusQuery>({
        query: EmailVerificationStatusDocument,
        fetchPolicy: 'no-cache',
      });
      if (attempt !== verifyAttemptRef.current) {
        return;
      }
      const verified = data?.me?.emailIsVerified ?? false;
      if (verified) {
        identifyCurrentUser();
        handleVerified();
      } else {
        setCheckFailed(true);
      }
    } catch (error) {
      if (attempt !== verifyAttemptRef.current) return;
      addError(error, { context: 'useEmailVerificationFlow/handleConfirmVerified' });
      // Still `checkFailed`, so surfaces that only read that keep holding the user; `checkErrored`
      // lets one that can tell the difference say "couldn't check" rather than "not verified".
      setCheckFailed(true);
      setCheckErrored(true);
    } finally {
      if (attempt === verifyAttemptRef.current) {
        setIsChecking(false);
      }
    }
  }, [client, identifyCurrentUser, handleVerified]);

  return {
    /** True once the account's address is verified, or when the gate's flag is off. */
    isEmailVerified,
    /** The publish-time gate is switched on. Off means publishing must not ask for verification. */
    isGateEnabled: isPublishEmailVerificationEnabled,
    /** The one question a publish surface asks: must this user verify before going live? */
    isVerificationRequired: isPublishEmailVerificationEnabled && !isEmailVerified,
    step,
    email,
    isSending,
    isChecking,
    checkFailed,
    /** The last status re-check errored, rather than coming back unverified. Implies `checkFailed`. */
    checkErrored,
    sendFailed,
    /** The last resend went out, so the surface can confirm it. Cleared by the next action. */
    didResend,
    resendCooldown,
    reset,
    cancel,
    handleVerify,
    handleResend,
    handleConfirmVerified,
  };
};
